import React, { useCallback, useState } from 'react';
import DiagnosticCriteriaTable from '../../components/DiagnosticCriteriaTable';
import YesNoToggle from '../../components/YesNoToggle';
import { useUserInput } from '../../context/UserInputContext';
import {
  cmmlWho2022Criteria,
  cmmlIcc2022Criteria,
  evaluateCmmlWho2022,
  evaluateCmmlIcc2022,
} from '../../data/diseasesData';

const SYSTEMS = [
  { id: 'who', label: 'WHO 2022' },
  { id: 'icc', label: 'ICC 2022' },
] as const;

const CmmlDiagnosticCriteria: React.FC = () => {
  const [activeSystem, setActiveSystem] = useState<'who' | 'icc'>('who');
  const { state, update } = useUserInput();
  const amcAtLeast1 = state.cmmlWhoAmcAtLeast1;

  // WHO's rule depends on the monocyte count, which is not itself a criterion
  const evaluateWho2022 = useCallback(
    (majorMet: number[], minorMet: number[]) => evaluateCmmlWho2022(majorMet, minorMet, amcAtLeast1),
    [amcAtLeast1]
  );

  return (
    <div className="cmml-diagnosis">
      <div className="criteria-system-tabs" role="tablist">
        {SYSTEMS.map((sys) => (
          <button
            key={sys.id}
            type="button"
            role="tab"
            className={`criteria-system-tabs__tab ${activeSystem === sys.id ? 'is-active' : ''}`}
            aria-selected={activeSystem === sys.id}
            onClick={() => setActiveSystem(sys.id)}
          >
            {sys.label}
          </button>
        ))}
      </div>

      {activeSystem === 'who' && (
        <>
          <YesNoToggle
            label={<>Monocytes ≥1 × 10⁹/L?</>}
            value={amcAtLeast1}
            onChange={(v) => update('cmmlWhoAmcAtLeast1', v)}
            hint="Decides how many supporting criteria are required"
          />
          <DiagnosticCriteriaTable
            criteria={cmmlWho2022Criteria}
            title="WHO 2022 Diagnostic Criteria for CMML"
            subtitle="Prerequisite criteria (all required) + supporting criteria"
            stateKey="cmmlWho2022Criteria"
            majorTitle="Prerequisite Criteria"
            minorTitle="Supporting Criteria"
            evaluateDiagnosis={evaluateWho2022}
          />
        </>
      )}

      {activeSystem === 'icc' && (
        <DiagnosticCriteriaTable
          criteria={cmmlIcc2022Criteria}
          title="ICC 2022 Diagnostic Criteria for CMML"
          subtitle="All 6 criteria required. Without clonality (criterion 4), the two alternative criteria must be met instead."
          stateKey="cmmlIcc2022Criteria"
          majorTitle="Criteria"
          minorTitle="Without Clonality (both required)"
          evaluateDiagnosis={evaluateCmmlIcc2022}
        />
      )}

      <section className="cmml-subtyping" dir="ltr">
        <h4 className="cmml-subtyping__title">CMML Subtyping & Subgrouping</h4>
        <div className="cmml-subtyping__grid">
          <div className="cmml-subtyping__card">
            <h5>Subtype — by WBC</h5>
            <ul>
              <li><strong>MD-CMML</strong> (Myelodysplastic): WBC &lt;13 × 10⁹/L</li>
              <li><strong>MP-CMML</strong> (Myeloproliferative): WBC ≥13 × 10⁹/L</li>
            </ul>
          </div>
          <div className="cmml-subtyping__card">
            <h5>Subgroup — by blast %</h5>
            <ul>
              <li><strong>CMML-1</strong>: PB blasts &lt;5% and BM blasts &lt;10%</li>
              <li><strong>CMML-2</strong>: PB blasts 5–19% or BM blasts 10–19%, or Auer rods</li>
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CmmlDiagnosticCriteria;
